import type { NostrEvent, NostrFilter } from "../types"

export interface NoteInteractions {
  reactions: NostrEvent[]
  reposts: NostrEvent[]
  quotes: NostrEvent[]
}

/** One REQ per relay for every interaction type (relays limit concurrent REQs). */
export function interactionFilters(noteId: string): NostrFilter[] {
  return [
    { kinds: [7], "#e": [noteId], limit: 400 },
    { kinds: [6], "#e": [noteId], limit: 200 },
    // NIP-18 quote reposts / quotes carry a `q` tag with the quoted note id.
    { kinds: [1], "#q": [noteId], limit: 100 },
  ]
}

/** True when a kind 1 note quotes the given note id. */
export function isQuoteOf(event: NostrEvent, noteId: string): boolean {
  if (event.kind !== 1) return false
  return event.tags.some((tag) => tag[0] === "q" && tag[1] === noteId)
}

export function reactionEmoji(event: NostrEvent): string {
  const content = event.content.trim()
  if (!content) return "+"
  // Custom emoji (NIP-30) are rendered by shortcode.
  const shortcode = event.tags.find((tag) => tag[0] === "emoji")?.[1]
  return shortcode ? `:${shortcode}:` : content
}

/** Per-emoji tally plus the current user's reaction, for the reaction tab. */
export function summariseReactions(
  events: NostrEvent[],
  me?: string,
): { total: number; byEmoji: Array<{ emoji: string; events: NostrEvent[] }>; mine?: string } {
  const groups = new Map<string, NostrEvent[]>()
  const latestByAuthor = new Map<string, NostrEvent>()
  let mine: string | undefined

  for (const event of events) {
    // One reaction per author: the newest one wins.
    const existing = latestByAuthor.get(event.pubkey)
    if (existing && existing.created_at >= event.created_at) continue
    latestByAuthor.set(event.pubkey, event)
    if (me && event.pubkey === me) mine = reactionEmoji(event)
  }

  for (const event of latestByAuthor.values()) {
    const emoji = reactionEmoji(event)
    const list = groups.get(emoji)
    if (list) list.push(event)
    else groups.set(emoji, [event])
  }

  const byEmoji = [...groups.entries()]
    .map(([emoji, groupEvents]) => ({ emoji, events: groupEvents }))
    .sort((a, b) => b.events.length - a.events.length)

  return { total: latestByAuthor.size, byEmoji, mine }
}

/** Unique authors of interaction events (one row per person). */
export function uniqueInteractionAuthors(events: NostrEvent[]): string[] {
  const seen = new Set<string>()
  const authors: string[] = []
  for (const event of events) {
    if (seen.has(event.pubkey)) continue
    seen.add(event.pubkey)
    authors.push(event.pubkey)
  }
  return authors
}
